import { FormHelperText } from '@material-ui/core';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';

export interface QtestMember {
  userEmail?: string;
  role?: string;
}

/** Emails listed more than once, lower-cased, in first-duplicated order. */
export function findDuplicateEmails(
  members: QtestMember[] | undefined,
): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const member of members ?? []) {
    const email = member?.userEmail?.trim().toLowerCase();
    if (!email) {
      continue;
    }
    if (seen.has(email)) {
      duplicates.add(email);
    } else {
      seen.add(email);
    }
  }
  return [...duplicates];
}

function duplicateEmailsMessage(
  members: QtestMember[] | undefined,
  duplicates: string[],
): string {
  return duplicates
    .map(email => {
      // Point at the entry the user already filled in, not the later copy.
      const first = (members ?? []).find(
        member => member?.userEmail?.trim().toLowerCase() === email,
      );
      const name = first?.userEmail?.trim() || email;
      const role = first?.role?.trim() || 'member';
      return `${name} is already listed as a ${role}. To change their role, please update the existing entry above.`;
    })
    .join(' ');
}

export function validateUniqueMemberEmails(
  members: QtestMember[] | undefined,
  validation: FieldValidation,
): void {
  const duplicates = findDuplicateEmails(members);
  if (duplicates.length > 0) {
    validation.addError(duplicateEmailsMessage(members, duplicates));
  }
}

/** Default array widget, plus a duplicate-email guard over the member list. */
export const UniqueMemberEmailsField = (
  props: FieldExtensionComponentProps<QtestMember[]>,
) => {
  const { ArrayField } = props.registry.fields;
  const duplicates = findDuplicateEmails(props.formData);
  return (
    <>
      <ArrayField {...props} />
      {/* Live hint; the scaffolder renders rawErrors itself once validation runs. */}
      {duplicates.length > 0 && !props.rawErrors?.length ? (
        <FormHelperText error>
          {duplicateEmailsMessage(props.formData, duplicates)}
        </FormHelperText>
      ) : null}
    </>
  );
};
